import { Component, Input, Output, EventEmitter, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-toggle-switch',
    templateUrl: './toggle-switch.component.html',
    styleUrls: ['./toggle-switch.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ToggleSwitchComponent),
            multi: true,
        },
    ],
})
export class ToggleSwitchComponent implements OnInit, ControlValueAccessor {
    @Input() label: string = '';
    @Input() disabled: boolean = false;
    @Input() accessForm: FormGroup;
    @Input() FormName: string;
    @Input() isEdited: boolean = false;
    @Output() toggleChange = new EventEmitter<boolean>();
    @Output() permissionsChange = new EventEmitter<any>();

    value: boolean = false;
    isReady: boolean = false;

    public full: boolean = false;

    public permissionsForm: FormGroup;

    private onChange = (value: boolean) => {};
    private onTouched = () => {};

    ngOnInit(): void {
        this.permissionsForm = this.accessForm.get(this.FormName) as FormGroup;
        if (this.isEdited) {
            this.value = this.accessForm.get(this.FormName).get('active').value;
        }
    }

    toggle() {
        if (this.disabled) return;

        this.full = false;
        this.value = !this.value;
        this.onChange(this.value);
        this.onTouched();
        this.toggleChange.emit(this.value);

        // Reset permissions when turned off
        if (!this.value) {
            this.permissionsForm.patchValue({
                active: false,
                read: false,
                edit: false,
                delete: false,
                fullAccess: false,
            });
        } else {
            this.permissionsForm.patchValue({
                active: true,
                read: false,
                edit: false,
                delete: false,
                fullAccess: false,
            });
        }
    }

    toggleFullAccess() {
        this.full = !this.full;

        this.permissionsForm.get('fullAccess').setValue(this.full);

        // When full access is enabled, enable all permissions
        if (this.permissionsForm.get('fullAccess').value) {
            this.permissionsForm.patchValue({
                active: true,
                read: true,
                edit: true,
                delete: true,
                fullAccess: true,
            });
        }

        if (!this.permissionsForm.get('fullAccess').value) {
            this.permissionsForm.patchValue({
                active: true,
                read: false,
                edit: false,
                delete: false,
                fullAccess: false,
            });
        }

        this.onPermissionChange();
    }

    onPermissionChange() {
        // Check if all permissions are enabled to auto-toggle full access
        const allEnabled = this.permissionsForm.get('read').value && this.permissionsForm.get('edit').value && this.permissionsForm.get('delete').value;

        if (allEnabled && !this.permissionsForm.get('fullAccess').value) {
            this.permissionsForm.get('fullAccess').setValue(true);
            this.full = true;
        } else if (!allEnabled && this.permissionsForm.get('fullAccess').value) {
            this.permissionsForm.get('fullAccess').setValue(false);
            this.full = false;
        }

        this.permissionsChange.emit(this.permissionsForm.value);
    }

    // ControlValueAccessor implementation
    writeValue(value: boolean): void {
        this.value = value;
    }

    registerOnChange(fn: (value: boolean) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
